import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import type { CurrentUser } from '@recruitee/account-types';
import { ENVIRONMENT, RtcErrorHandler } from '@recruitee/common';

import { CurrentUserSelectors } from './store/current-user/current-user.store';

const DUPLICATE_THRESHOLD_MS = 500;

@Injectable({ providedIn: 'root' })
export class DuplicateTrackerInterceptorService implements HttpInterceptor {
  private lastRequestMap = new Map<string, { time: number }>();
  private excludedUrls: RegExp[] = [
    /\/search\/new\/fs_counts/, // this is POST request with different payloads
    // this GET request is send after /tracking/activity_subscriptions/update_activity_subscriptions
    // because backend doesn't send any response that would allow us to verify if change was successful
    /\/tracking\/activity_subscriptions/,
    /\/elements/,
  ];
  private currentAdmin: CurrentUser;
  private environment = inject(ENVIRONMENT, { optional: true });

  constructor(
    private errorHandler: RtcErrorHandler,
    private store: Store,
  ) {
    this.store.select(CurrentUserSelectors.data).subscribe(currentAdmin => {
      this.currentAdmin = currentAdmin;
    });
  }

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (
      this.environment?.trackDuplicateHttpRequests &&
      !this.isExcluded(req) &&
      !this.isExcludedAdmin()
    ) {
      this.trackDuplicates(req);
    }
    return next.handle(req);
  }

  private isExcluded(req: HttpRequest<any>): boolean {
    return this.excludedUrls.some(excludeRegex => excludeRegex.test(req.urlWithParams));
  }

  // exclude E2E users
  private isExcludedAdmin(): boolean {
    return this.currentAdmin.email.startsWith(`e2e+`);
  }

  private trackDuplicates(req: HttpRequest<any>): void {
    const keyWithParams = `${req.method}:${req.urlWithParams}`;
    const previousRequest = this.lastRequestMap.get(keyWithParams);
    const currentTime = Date.now();
    this.lastRequestMap.set(keyWithParams, { time: currentTime });
    if (!previousRequest) return;

    const diff = currentTime - previousRequest.time;
    if (diff > DUPLICATE_THRESHOLD_MS) return;

    const url = req.urlWithParams.replace(/\/(\d+?)(\/|$)/g, '/id/');
    this.errorHandler.trace(`Duplicate call ${req.method}:"${url}"`, false);
    console.warn(`Duplicate call ${req.method}:"${req.urlWithParams}". Diff: ${diff}ms`);
  }
}
